import type {
  AuditPayload,
  AuditRow,
  AuditScores,
  AdvertiserMatch,
  AutomationTierType,
  ClientSummary,
  DoctrineEntry,
  OnboardSearchResult,
  OriginType,
  PortfolioEntry,
  ScoreBlock,
  ThemeDef
} from '@blisspointmedia/bpm-types/dist/CampaignAudit';
import { AutomationTier, Origin } from '@blisspointmedia/bpm-types/dist/CampaignAudit';
import {
  awaitJSON,
  TvadCampaignAuditLambdaFetch,
  fetchCometSelfInvoke
} from '../../utils/fetch-utils';
import { FetchError } from '../../utils/fetch-error';

export type {
  AuditPayload,
  AuditRow,
  AuditScores,
  AdvertiserMatch,
  AutomationTierType,
  ClientSummary,
  DoctrineEntry,
  OnboardSearchResult,
  OriginType,
  PortfolioEntry,
  ScoreBlock,
  ThemeDef
};

export { AutomationTier, Origin };

export const fetchAudit = async (cid: string): Promise<AuditPayload | null> => {
  try {
    const res = await TvadCampaignAuditLambdaFetch('/audit', { params: { cid } });
    return await awaitJSON<AuditPayload>(res);
  } catch (e) {
    if (e instanceof FetchError && e.status === 404) return null;
    throw e;
  }
};

// Audit assembly makes many external DSP/ad-server calls and can exceed API Gateway's
// hard 30s limit, so POST /audit/run runs as a Comet self-invoke (trigger ack + S3 poll, no
// gateway timeout) rather than a synchronous endpoint.
//
// The kickoff invokes the worker with InvocationType 'Event' (1MB async payload limit), so the
// payload must stay tiny — send only the cid. The worker fetches all BPM data server-side
// (assembleAudit -> fetchBpmData); passing it from the client both duplicated that work and blew
// the 1MB limit for larger clients (e.g. Rover's ~3MB BPM blob).
export const runAudit = async (cid: string): Promise<AuditPayload> =>
  fetchCometSelfInvoke<{ cid: string }, AuditPayload>(
    TvadCampaignAuditLambdaFetch,
    '/audit/run',
    { cid },
    { noTimeout: true }
  );

// Extra fields for the "accepted" disposition (a known, deliberate gap): the finding is excluded from
// scoring but kept visible. `answer` carries the (required) rationale; revisitAt/nextStep are optional.
export interface SaveAnswerExtras {
  disposition?: AuditRow['disposition'];
  revisitAt?: string;
  nextStep?: string;
}

// Save a manual answer. Pass a verdict to override the auto read; omit it (note-only, F31) to attach
// a note while leaving the auto verdict in place. Pass `extras.disposition = 'accepted'` to close the
// finding as a known, deliberate gap (excluded from scoring, kept visible) with `answer` as rationale.
export const saveManualAnswer = async (
  cid: string,
  id: number,
  verdict: AuditRow['verdict'],
  answer?: string,
  extras?: SaveAnswerExtras
): Promise<AuditPayload> => {
  const res = await TvadCampaignAuditLambdaFetch('/audit/answer', {
    method: 'POST',
    body: { cid, id, verdict, answer, ...extras }
  });
  return awaitJSON<AuditPayload>(res);
};

// Revert a row to its auto read (F16): drops any manual answer/override for the row.
export const revertToAuto = async (cid: string, id: number): Promise<AuditPayload> => {
  const res = await TvadCampaignAuditLambdaFetch('/audit/answer', {
    method: 'POST',
    body: { cid, id, clear: true }
  });
  return awaitJSON<AuditPayload>(res);
};

// Mark a flagged row "addressed" (or clear it) for a run (CAB-8). Returns the run's authoritative
// addressed-row set so the caller can reconcile its optimistic toggle.
export const saveAction = async (
  cid: string,
  runId: number,
  rowId: number,
  addressed: boolean
): Promise<number[]> => {
  const res = await TvadCampaignAuditLambdaFetch('/audit/action', {
    method: 'POST',
    body: { cid, runId, rowId, addressed }
  });
  const { addressedRowIds } = await awaitJSON<{ addressedRowIds: number[] }>(res);
  return addressedRowIds;
};

export const fetchDoctrine = async (): Promise<DoctrineEntry[]> => {
  const res = await TvadCampaignAuditLambdaFetch('/doctrine');
  return awaitJSON<DoctrineEntry[]>(res);
};

// Per-client, question-level override (turn a question "off"/"on"). Returns the refreshed audit.
export const saveOverride = async (
  cid: string,
  id: number,
  notApplicable: boolean,
  reason?: string
): Promise<AuditPayload> => {
  const res = await TvadCampaignAuditLambdaFetch('/audit/override', {
    method: 'POST',
    body: { cid, id, notApplicable, reason }
  });
  return awaitJSON<AuditPayload>(res);
};

// Client-agnostic editable guidance. Target a question (questionId) OR a theme recommendation
// (themeId). Returns the refreshed, overlay-merged doctrine.
export interface GuidanceRecordInput {
  basis?: string;
  expectation?: string;
  rationale?: Partial<Record<NonNullable<AuditRow['verdict']>, string>>;
  calibration?: string;
  humanContext?: string;
  themeRecommendation?: string;
}

export const saveGuidance = async (
  target: { questionId: number } | { themeId: string },
  guidance: GuidanceRecordInput
): Promise<DoctrineEntry[]> => {
  const res = await TvadCampaignAuditLambdaFetch('/guidance', {
    method: 'POST',
    body: { ...target, guidance }
  });
  return awaitJSON<DoctrineEntry[]>(res);
};

export const getClients = async (): Promise<ClientSummary[]> => {
  const res = await TvadCampaignAuditLambdaFetch('/clients');
  return awaitJSON<ClientSummary[]>(res);
};

export const getPortfolio = async (): Promise<PortfolioEntry[]> => {
  const res = await TvadCampaignAuditLambdaFetch('/portfolio');
  return awaitJSON<PortfolioEntry[]>(res);
};

export const searchOnboard = async (q: string): Promise<OnboardSearchResult> => {
  const res = await TvadCampaignAuditLambdaFetch('/onboard/search', { params: { q } });
  return awaitJSON<OnboardSearchResult>(res);
};

export const confirmOnboard = async (
  bpmCid: string,
  bpmName: string,
  runsLinear: boolean,
  nexxenId?: string,
  ftAdvertiserId?: string
): Promise<{ cid: string; name: string; configured: boolean }> => {
  const body: Record<string, unknown> = {
    cid: bpmCid,
    slug: bpmCid,
    name: bpmName,
    runsLinear
  };
  if (nexxenId) {
    body.dsp = { advertiserId: nexxenId };
  }
  if (ftAdvertiserId) {
    body.flashtalking = { advertiserId: Number(ftAdvertiserId) };
  }
  const res = await TvadCampaignAuditLambdaFetch('/onboard/confirm', { method: 'POST', body });
  return awaitJSON<{ cid: string; name: string; configured: boolean }>(res);
};
